import { css, HelpIcon, styled } from 'canoe';
import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import debounce from 'lodash/debounce';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { ContentMetadata, ContentType } from '@candu-ai/content/types';
import { TextButton, ValueBox, ValueSelectInput } from '@candu-ai/craft/ui';
import { useWorkspacePath } from 'commons/hooks';
import { CONTENT_PUBLISHED_STATES } from 'commons/models';
import { ToolboxErrorMessage } from 'craft/components/ToolboxErrorMessage';
import { TriggerHotspotGroupAction, TriggerModalAction, TriggerTourAction } from 'craft/types';
import { contentPath } from 'dashboard/pages/Content';
import ContentPublishedState from 'dashboard/pages/Content/components/ContentPublishedState';

import { InteractionFieldProps } from './interactionFields';

interface StateProps {
  semaphoreOnly: boolean;
  content: any;
}

const State = styled(ContentPublishedState)<StateProps>`
  height: 8px;
  margin-left: 6px;
  margin-top: 5px;
  width: 8px;
`;

const OptionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    background-color: ${theme.colors.BerryBlueL30};
    color: ${theme.colors.BerryBlue};
    padding: 7px 9px;

    svg {
      margin-right: 8px;
    }
  `}
`;

type Item = {
  value: string;
  label: string;
  id: string;
  prefix?: React.ReactNode;
  intent?: string;
  disabled?: boolean;
};

type ActionTypes = TriggerModalAction | TriggerTourAction | TriggerHotspotGroupAction;
interface ContentTypeProp {
  contentType: ContentType;
}

const TriggerContentField = ({
  contentType,
  defaultValue,
  onChange,
}: InteractionFieldProps<ActionTypes['handler']> & ContentTypeProp) => {
  const initialDefaultValue = useRef(defaultValue);
  const workspacePath = useWorkspacePath();

  const { getAll } = useCreatePayload(ENDPOINTS.contentMetadata);
  const payload = getAll({
    query: {
      finder: 'byContentType',
      contentType,
    },
  });

  const selectedValuePayload = getAll({
    query: {
      finder: 'bySlug',
      slug: initialDefaultValue.current?.slug,
    },
  });
  const [searchValue, setSearchValue] = useState('');

  const { data: defaultValueData, isLoading: isLoadingDefaultValue } = useQuery(
    createCacheKey(selectedValuePayload),
    () =>
      fetchResource<ContentMetadata[]>(selectedValuePayload).then(
        (defaultValues) => defaultValues[0],
      ),
    { enabled: !!initialDefaultValue.current?.slug },
  );

  const { data, isLoading } = useQuery(createCacheKey(payload), () =>
    fetchResource<ContentMetadata[]>(payload).then((contentList) =>
      contentList.sort((a, b) => {
        return moment(b.lastModifiedAt).millisecond() - moment(a.lastModifiedAt).millisecond();
      }),
    ),
  );

  // append to the rest of the array the default selected value
  const options = useMemo(
    () => [
      ...(defaultValueData && !data?.find((d) => d.slug === defaultValue?.slug)
        ? [{ value: defaultValueData.slug, label: defaultValueData.slug, id: defaultValueData.id }]
        : []),
      ...(data || []).map((content) => ({
        value: content.slug,
        label: content.name,
        id: content.id,
        prefix: (
          <State
            semaphoreOnly
            content={{
              lastModifiedAt: content.lastModifiedAt,
              lastPublishedAt: content.lastPublishedAt,
              publishedState: content.lastPublishedAt
                ? CONTENT_PUBLISHED_STATES.PUBLISHED
                : CONTENT_PUBLISHED_STATES.DRAFT,
            }}
          />
        ),
      })),
    ],
    [data, defaultValueData],
  );

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }
    return options.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, options, defaultValueData]);

  const publishedContentValue = !!data?.find(
    (value) => value.slug === defaultValue?.slug && value.lastPublishedAt,
  );

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  const clearSearchValue = () => setSearchValue('');

  const onSelectValue = (select: { value: string } | null) => {
    onChange({ ...defaultValue, slug: select?.value });
  };

  const selectedItem = filteredOptions.find(({ value }) => value === defaultValue?.slug);

  return (
    <>
      <ValueBox isInGroup={{ isLast: true }}>
        <ValueSelectInput<Item>
          scale="small"
          searchable
          onOpeningChange={(isOpen) => {
            if (!isOpen) {
              clearSearchValue();
            }
          }}
          calloutOffset="xxs"
          searchInputProps={{ placeholder: 'Search Content' }}
          placeholder="Content to display"
          onFilter={updateSearchValue}
          onClearFilter={clearSearchValue}
          optionsPrefix={
            <OptionContainer>
              <HelpIcon color="BerryBlue" size="s" />
              Only published content will go live.
            </OptionContainer>
          }
          items={filteredOptions}
          loading={isLoading || isLoadingDefaultValue}
          selectedItem={selectedItem}
          onSelect={onSelectValue}
        />
      </ValueBox>
      {publishedContentValue && defaultValue?.slug && selectedItem?.value && (
        <TextButton
          onClick={() => window.open(workspacePath(contentPath(selectedItem?.id)), '_blank')}
        >
          Open Content
        </TextButton>
      )}
      {!publishedContentValue && defaultValue?.slug && !isLoading && (
        <ToolboxErrorMessage message="The selected Content is not published" />
      )}
    </>
  );
};

export const TriggerModalField = (props: InteractionFieldProps<ActionTypes['handler']>) => {
  return <TriggerContentField {...props} contentType={ContentType.Modal} />;
};

export const TriggerTourField = (props: InteractionFieldProps<ActionTypes['handler']>) => {
  return <TriggerContentField {...props} contentType={ContentType.Tour} />;
};

export const TriggerHotspotGroupField = (props: InteractionFieldProps<ActionTypes['handler']>) => {
  return <TriggerContentField {...props} contentType={ContentType.HotspotGroup} />;
};
